import { useId, useRef, useState, useEffect, useCallback } from 'react';
import { offset, flip, shift, useFloating, autoUpdate, arrow, useClick, useDismiss, useHover, safePolygon, useInteractions } from '@floating-ui/react';
import { getFlags } from '../utils/flags.js';

/**
 * A hook that returns functions to apply to a target and a floating container attached to the target
 * @see https://fpl.figma.design/primitives/popover/#usepopover
 */
function usePopover({ type, softDismiss, provideOwnDismiss, provideOwnClick, isOpen, onOpenChange: onOpenChangeProp, openOnHover = false, allowScrimPassthrough = false, offset: offsetProp = 8, arrowPadding = 0, middleware, config2025CuratorHacks, disableTransform, ...options }) {
    const tmpId = useId();
    const arrowRef = useRef(null);
    const [containerId, setContainerId] = useState(tmpId);
    const [isHoverOpen, setIsHoverOpen] = useState(false);
    const { fpl_popover_shift } = getFlags();
    useEffect(() => {
        if (!isOpen)
            setIsHoverOpen(false);
    }, [isOpen]);
    const onOpenChange = useCallback((nextIsOpen, event, reason) => {
        setIsHoverOpen(nextIsOpen && reason === 'hover');
        onOpenChangeProp?.(nextIsOpen, event, reason);
    }, [onOpenChangeProp]);
    const defaultMiddleware = [offset(offsetProp)];
    const flipMiddleware = flip({ padding: 8, fallbackAxisSideDirection: 'start' });
    const shiftMiddleware = shift({ padding: 8 });
    if (fpl_popover_shift) {
        if (options.placement?.includes('-')) {
            defaultMiddleware.push(flipMiddleware, shiftMiddleware);
        }
        else {
            defaultMiddleware.push(shiftMiddleware, flipMiddleware);
        }
    }
    else {
        defaultMiddleware.push(flipMiddleware);
    }
    const providedMiddleware = middleware ?? defaultMiddleware;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { refs, floatingStyles, context } = useFloating({
        ...options,
        onOpenChange,
        middleware: providedMiddleware.concat([arrow({ element: arrowRef, padding: arrowPadding })]),
        open: isOpen,
        whileElementsMounted: (reference, floating, update) => {
            if (!isOpen)
                return () => { };
            return autoUpdate(reference, floating, update, { animationFrame: allowScrimPassthrough });
        },
        transform: disableTransform !== true,
    });
    const useSoftDismiss = (() => {
        // Always disable soft dismiss if the popover is open because it's hovered. Otherwise the
        // scrim interferes with the hover pointer events and causes oscillation
        if (isHoverOpen)
            return false;
        // If the type is list box or menu, by default make soft dismiss true unless explicitly overridden
        if (softDismiss === undefined) {
            return type === 'listbox' || type === 'menu' || type === 'tooltip';
        }
        return softDismiss;
    })();
    const click = useClick(context, { enabled: type !== 'tutorial' && !provideOwnClick });
    const dismiss = useDismiss(context, {
        enabled: !provideOwnDismiss,
        outsidePress: (event) => {
            if (event.button !== 0)
                return false;
            return useSoftDismiss;
        },
    });
    const hover = useHover(context, {
        enabled: openOnHover,
        move: false,
        delay: { open: 500, close: 0 },
        handleClose: safePolygon({ requireIntent: false, blockPointerEvents: true, buffer: 1 }),
    });
    const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, hover]);
    const lastInputContainerRef = useRef(null);
    const memoedContainerRef = useRef(() => { });
    const getContainerProps = useCallback(({ style, ...props } = {}) => {
        if (props.id)
            setContainerId(props.id);
        // Use cache key based on the props.ref identity to memoize ref callbacks
        if (lastInputContainerRef.current !== props.ref) {
            lastInputContainerRef.current = props.ref;
            memoedContainerRef.current = (node) => {
                refs.setFloating(node);
                if (props.ref) {
                    if (typeof props.ref === 'function') {
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                        props.ref(node);
                    }
                    else if (props.ref && 'current' in props.ref) {
                        props.ref.current = node;
                    }
                }
            };
        }
        return {
            isOpen,
            type,
            context,
            useSoftDismiss,
            allowScrimPassthrough,
            id: containerId,
            style: { ...style, ...floatingStyles },
            config2025CuratorHacks,
            ...props,
            ...getFloatingProps({}),
            ref: memoedContainerRef.current,
        };
    }, [
        isOpen,
        type,
        context,
        allowScrimPassthrough,
        useSoftDismiss,
        containerId,
        floatingStyles,
        config2025CuratorHacks,
        getFloatingProps,
        refs,
    ]);
    const getArrowProps = useCallback(() => ({
        ref: arrowRef,
        context,
    }), [context, arrowRef]);
    const getTriggerProps = useCallback((props = {}) => {
        const ref = refs.setReference;
        if (type === 'tutorial') {
            const { onClick, ...triggerProps } = getReferenceProps({
                ref,
                'aria-details': containerId,
                // 'aria-haspopup': 'dialog'
            });
            return triggerProps;
        }
        return getReferenceProps({
            ...props,
            ref,
            'aria-controls': containerId,
            'aria-haspopup': type === 'tooltip' ? 'dialog' : type,
            'aria-expanded': isOpen,
            'aria-describedby': type === 'tooltip' ? containerId : undefined,
        });
    }, [containerId, getReferenceProps, isOpen, refs.setReference, type]);
    return {
        getTriggerProps: getTriggerProps,
        getArrowProps,
        getContainerProps,
        context,
        containerId,
    };
}

export { usePopover };
//# sourceMappingURL=popover-primitive.hooks.js.map
